
export const getSeverityColor = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'text-red-500';
    case 'high':
      return 'text-orange-500';
    case 'medium':
      return 'text-yellow-500';
    case 'low':
      return 'text-green-500';
    default:
      return 'text-gray-500';
  }
};

export const formatEventType = (eventType: string) => {
  return eventType.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// XSS sanitization helper for text content
export const sanitizeContent = (input: string): string => {
  // Create a new div element
  const tempDiv = document.createElement('div');
  // Set the input as text content (browser handles HTML encoding)
  tempDiv.textContent = input;
  // Return the safely encoded HTML
  return tempDiv.innerHTML;
};

// Validate form input against XSS patterns
export const validateAgainstXss = (input: string): boolean => {
  const dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+=/gi,
    /data:/gi,
    /vbscript:/gi,
    /expression\(/gi,
    /eval\(/gi,
    /fromcharcode\(/gi
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(input));
};

// Enhanced CSRF token generation with entropy from multiple sources
export const generateCsrfToken = (): string => {
  // Generate random values from crypto API
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  
  // Add timestamp for additional uniqueness
  const timestamp = new Date().getTime().toString(36);
  
  // Combine crypto random bytes with timestamp
  const token = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('') + timestamp;
  
  // Add token expiration (30 minutes from now)
  const expiration = new Date();
  expiration.setMinutes(expiration.getMinutes() + 30);
  sessionStorage.setItem('csrfTokenExpiration', expiration.getTime().toString());
  
  return token;
};

// Enhanced CSRF validation with expiration check
export const validateCsrfToken = (token: string, storedToken: string): boolean => {
  if (!token || !storedToken || token !== storedToken) {
    return false;
  }
  
  // Check if token has expired
  const expirationTimeStr = sessionStorage.getItem('csrfTokenExpiration');
  if (!expirationTimeStr) {
    return false;
  }
  
  const expirationTime = parseInt(expirationTimeStr, 10);
  const currentTime = new Date().getTime();
  
  return currentTime < expirationTime;
};

// Password strength checker
export const checkPasswordStrength = (password: string): {score: number; feedback: string} => {
  let score = 0;
  const feedback = [];
  
  // Length check
  if (password.length < 8) {
    feedback.push('Password is too short');
  } else {
    score += password.length > 12 ? 2 : 1;
  }
  
  // Character variety checks
  if (/[A-Z]/.test(password)) score += 1;
  else feedback.push('Add uppercase letters');
  
  if (/[a-z]/.test(password)) score += 1;
  else feedback.push('Add lowercase letters');
  
  if (/[0-9]/.test(password)) score += 1;
  else feedback.push('Add numbers');
  
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  else feedback.push('Add special characters');
  
  // Check for common patterns
  if (/^(123|abc|qwerty|password|admin|user)/i.test(password)) {
    score -= 1;
    feedback.push('Avoid common patterns');
  }
  
  // Check for repeated characters
  if (/(.)\1{2,}/.test(password)) {
    score -= 1;
    feedback.push('Avoid repeated characters');
  }
  
  return {
    score: Math.max(0, Math.min(5, score)), // Score 0-5
    feedback: feedback.join(', ')
  };
};

// Detect potential security threats in user input
export const detectSecurityThreats = (input: string): {isThreat: boolean; reason?: string} => {
  // Check for SQL injection patterns
  const sqlPatterns = [
    /'\s*OR\s*'1'\s*=\s*'1/i,
    /'\s*OR\s*1\s*=\s*1/i,
    /--/,
    /;\s*DROP\s+TABLE/i,
    /;\s*DELETE\s+FROM/i,
    /UNION\s+SELECT/i
  ];
  
  if (sqlPatterns.some(pattern => pattern.test(input))) {
    return { isThreat: true, reason: 'Potential SQL injection detected' };
  }
  
  // Check for command injection patterns
  const commandPatterns = [
    /\|\s*rm\s/i,
    /`.*`/,
    /\$\(.+\)/,
    /;\s*rm\s/i,
    />\s*\/dev\/null/i
  ];
  
  if (commandPatterns.some(pattern => pattern.test(input))) {
    return { isThreat: true, reason: 'Potential command injection detected' };
  }
  
  // Check for path traversal attempts
  const pathTraversalPatterns = [
    /\.\.\//,
    /\.\.\\/ 
  ];
  
  if (pathTraversalPatterns.some(pattern => pattern.test(input))) {
    return { isThreat: true, reason: 'Potential path traversal detected' };
  }
  
  return { isThreat: false };
};

// Sanitize URL parameters
export const sanitizeUrlParams = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    
    // Sanitize each parameter
    params.forEach((value, key) => {
      params.set(key, sanitizeContent(value));
    });
    
    // Rebuild URL with sanitized parameters
    urlObj.search = params.toString();
    return urlObj.toString();
  } catch (e) {
    // If parsing fails, return the original URL
    return url;
  }
};
